'use client';

import { Button, CircularProgress, Divider, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { InverseButton } from '@parkchirp/ui-library';
import React, { useCallback, useEffect, useState } from 'react';

import { dayjs } from '../../utils';

type Selection = {
	value: string;
	label: string;
};

interface Props {
	title: string | null;
	reportTypes: Selection[];
	handleDismiss: () => void;
	confirmText: string | null;
	dismissText: string | null;
	fileOptions: string[];
	subId: number;
	useReportDownload: () => any;
}

const style = {
	background: 'none',
	border: 0,
	color: 'blue',
	fontSize: 'inherit',
	cursor: 'pointer',
	marginBottom: '20px',
};

export const AccountHistoryExportModal = ({
	title,
	handleDismiss,
	confirmText,
	dismissText,
	reportTypes,
	fileOptions,
	subId,
	useReportDownload,
}: Props): JSX.Element => {
	const [reportType, setReportType] = useState(reportTypes[0].value);
	const [fileType, setFileType] = useState(fileOptions[0]);
	const theme = useTheme();
	const [downloading, setDownloading] = useState(false);
	const { downloadReport } = useReportDownload();
	const [accountId, setAccountId] = useState<number | null>(null);
	const [facilityId, setFacilityId] = useState<string | null>(null);

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		setAccountId(Number(urlParams.get('accountId')));
		setFacilityId(urlParams.get('facility'));
	}, []);

	const getDownloadParams = useCallback(() => {
		const baseParams = {
			accountId,
			asOfDate: dayjs().format('YYYY-MM-DD'),
			facilityId,
			subscriberId: subId.toString(),
		};

		let summaryParams = {};
		if (reportType === 'OutstandingBalanceSummaryReport') {
			summaryParams = { contactInfo: true, currentBalances: true, subscriptionStatus: true };
		}

		return {
			...baseParams,
			...summaryParams,
		};
	}, [accountId, facilityId, subId, reportType]);

	const params = getDownloadParams();

	return (
		<div style={{ width: '600px' }}>
			<Typography data-cy='modal-title-text' fontWeight='bold' variant='h6'>
				{title}
			</Typography>
			<Divider variant='middle' />
			<br />
			<Grid spacing={2} container>
				<Grid item xs={12}>
					<InputLabel>
						Report Type<span style={{ color: 'red' }}>*</span>
					</InputLabel>
					<Select required fullWidth value={reportType} onChange={(e: any) => setReportType(e.target.value)}>
						{reportTypes.map((type, index) => (
							<MenuItem key={index} value={type.value}>
								{type.label}
							</MenuItem>
						))}
					</Select>
				</Grid>
				<Grid item xs={12}>
					<InputLabel>
						File Type<span style={{ color: 'red' }}>*</span>
					</InputLabel>
					<Select value={fileType} required fullWidth onChange={(e: any) => setFileType(e.target.value)}>
						{fileOptions.map((type, index) => (
							<MenuItem key={index} value={type}>
								{type}
							</MenuItem>
						))}
					</Select>
				</Grid>
				<Grid item xs={12} style={{ paddingBottom: '16px' }}>
					<a
						target='_blank'
						style={style}
						href={`/reports/?facility=${facilityId}`}
						data-cy='facilities-table-name-link'
						rel='noreferrer'
					>
						View Reports Page
					</a>
				</Grid>
			</Grid>
			<Grid className='pca__ratesForm-containerPadding' spacing={2} container>
				<Grid item xs={6}>
					<InverseButton
						fullWidth
						onClick={handleDismiss}
						name={dismissText}
						theme={theme}
						data-cy='modal-cancel-button'
					/>
				</Grid>
				<Grid item xs={6}>
					<Button
						fullWidth
						onClick={() => {
							downloadReport(fileType.toLowerCase(), {
								params,
								reportType,
								setIsDownloadingCsv: setDownloading,
								setIsDownloadingPdf: setDownloading,
								handleValidation: () => true,
							});
						}}
						data-cy='modal-confirm-button'
					>
						{confirmText}
						{downloading && (
							<CircularProgress style={{ height: '20px', width: '20px', color: '#FCE100' }} />
						)}
					</Button>
				</Grid>
			</Grid>
		</div>
	);
};
